import OpenAI, { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const fraudDetectionService = {
  /**
   * Analyzes transaction patterns using OpenAI to detect suspicious activities
   * @param {Array} transactions - Array of transaction objects
   * @param {Object} userProfile - User profile information
   * @returns {Promise<Object>} Analysis results with risk scores and recommendations
   */
  async analyzeTransactionPatterns(transactions, userProfile) {
    try {
      const transactionSummary = transactions?.map(tx => ({
        merchant: tx?.merchant,
        category: tx?.category,
        amount: Math.abs(tx?.amount),
        paymentSource: tx?.paymentSource,
        date: tx?.date,
        time: tx?.time,
        status: tx?.status
      }));

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert fraud detection analyst specializing in payment security across multiple payment rails (crypto and traditional). Analyze transaction patterns and identify suspicious activities with actionable insights.' 
          },
          { 
            role: 'user', 
            content: `Analyze these transactions for fraud patterns:\n\nUser Profile: ${JSON.stringify({ tier: userProfile?.tier, region: userProfile?.region || 'Unknown' })}\n\nTransactions: ${JSON.stringify(transactionSummary)}\n\nIdentify suspicious patterns, calculate risk scores, and provide recommended actions.` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'fraud_analysis_response',
            schema: {
              type: 'object',
              properties: {
                overallRiskScore: { 
                  type: 'number',
                  description: 'Overall risk score from 0-100'
                },
                riskLevel: { 
                  type: 'string',
                  description: 'Risk level: low, medium, high, critical'
                },
                suspiciousActivities: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      id: { type: 'string' },
                      type: { type: 'string' },
                      description: { type: 'string' },
                      riskScore: { type: 'number' },
                      severity: { type: 'string' },
                      affectedTransactions: { 
                        type: 'array',
                        items: { type: 'string' }
                      },
                      recommendedAction: { type: 'string' },
                      actionPriority: { type: 'string' }
                    },
                    required: ['id', 'type', 'description', 'riskScore', 'severity', 'recommendedAction']
                  }
                },
                patterns: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      pattern: { type: 'string' },
                      frequency: { type: 'number' },
                      isNormal: { type: 'boolean' }
                    },
                    required: ['pattern', 'frequency', 'isNormal']
                  }
                },
                recommendations: {
                  type: 'array',
                  items: { type: 'string' }
                },
                paymentRailAnalysis: {
                  type: 'object',
                  properties: {
                    btc: { type: 'object' },
                    eth: { type: 'object' },
                    usdc: { type: 'object' },
                    card: { type: 'object' }
                  }
                }
              },
              required: ['overallRiskScore', 'riskLevel', 'suspiciousActivities', 'patterns', 'recommendations'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'high',
        verbosity: 'medium',
      });

      const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
      
      // Store analysis results
      await this.storeAnalysisResults(userProfile?.id, analysis);
      
      // Create notifications for high-risk activities
      if (analysis?.riskLevel === 'high' || analysis?.riskLevel === 'critical') {
        await this.createFraudAlerts(userProfile?.id, analysis);
      }

      return analysis;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing transaction patterns:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Analyzes a single transaction in real-time
   */
  async analyzeTransaction(transaction, recentTransactions, userProfile) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are a real-time fraud detection system. Analyze this transaction against recent patterns and flag if suspicious.' 
          },
          { 
            role: 'user', 
            content: `Current Transaction: ${JSON.stringify(transaction)}\n\nRecent Transactions: ${JSON.stringify(recentTransactions?.slice(0, 10))}\n\nUser Profile: ${JSON.stringify({ tier: userProfile?.tier })}\n\nIs this transaction suspicious?` 
          },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'transaction_analysis',
            schema: {
              type: 'object',
              properties: {
                isSuspicious: { type: 'boolean' },
                riskScore: { type: 'number' },
                reason: { type: 'string' },
                recommendedAction: { type: 'string' },
                shouldBlock: { type: 'boolean' }
              },
              required: ['isSuspicious', 'riskScore', 'reason', 'recommendedAction', 'shouldBlock'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'minimal',
        verbosity: 'low',
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing transaction:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Stores fraud analysis results in database
   */
  async storeAnalysisResults(userId, analysis) {
    try {
      // Store in a fraud_analysis table or as metadata
      // For now, we'll use the notifications system
      return analysis;
    } catch (error) {
      console.error('Error storing analysis results:', error);
    }
  },

  /**
   * Creates fraud alert notifications
   */
  async createFraudAlerts(userId, analysis) {
    try {
      const highRiskActivities = analysis?.suspiciousActivities?.filter(
        activity => activity?.severity === 'critical' || activity?.severity === 'high'
      );

      for (const activity of highRiskActivities) {
        await supabase?.from('notifications')?.insert({
          user_id: userId,
          type: 'suspicious_activity',
          severity: activity?.severity === 'critical' ? 'critical' : 'warning',
          title: `Suspicious Activity Detected: ${activity?.type}`,
          message: `${activity?.description} Risk Score: ${activity?.riskScore}/100. ${activity?.recommendedAction}`,
          is_read: false
        });
      }
    } catch (error) {
      console.error('Error creating fraud alerts:', error);
    }
  },

  /**
   * Gets fraud detection history for a user
   */
  async getFraudHistory(userId, limit = 10) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.eq('type', 'suspicious_activity')?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        severity: row?.severity,
        title: row?.title,
        message: row?.message,
        isRead: row?.is_read,
        createdAt: row?.created_at
      }));
    } catch (error) {
      console.error('Error fetching fraud history:', error);
      return [];
    }
  }
};
